/**
 * 레슨 예약 정책 객체
 *
 * 복잡한 비즈니스 로직을 응집:
 * - 예약 검증 (티켓, 중복, 규칙)
 * - 언어별 예약 수 조회
 * - 티켓 수량 조회
 * - 예약 변경 가능 여부
 */

import { type CurriculumType, lessonEntitySchema } from '@server/schemas'
import { z } from 'zod'

import { API_CURRICULUM_TYPE_MAP, ERROR_MESSAGE_MAP } from '../../model/constants/card'
import type { MinimalLessonEntity, MinimalTicketEntity, MinimalUserEntity } from '../../model/types/view-model'
import { isChangeNotAllowedForLesson } from '../helpers/card'

export class LessonBookingPolicy {
  private readonly bookingValidationSchema = lessonEntitySchema.extend({
    teacherId: z.number().min(0, { message: ERROR_MESSAGE_MAP.CANNOT_BOOKING }),
    classId: z.number({ message: ERROR_MESSAGE_MAP.CANNOT_BOOKING }),
  })

  constructor(
    private readonly lesson: MinimalLessonEntity,
    private readonly user: MinimalUserEntity,
    private readonly tickets: MinimalTicketEntity[],
    private readonly curriculumType: CurriculumType,
  ) {}

  /**
   * 예약 가능 여부 검증
   *
   * 체크 항목:
   * 1. teacherId, classId 유효성
   * 2. 티켓 수량
   * 3. 중복 예약 (무제한 티켓)
   * 4. 더블팩 예약 (ENJP)
   */
  async validateBooking(): Promise<z.infer<typeof this.bookingValidationSchema>> {
    const parsedEntity = this.bookingValidationSchema.parse(this.lesson)

    // 티켓 수량 체크
    if (this.remainTicketCount === 0) {
      throw new Error('NOT_ENOUGH_TICKET')
    }

    // 중복 예약 체크 (무제한 티켓 + 이미 예약 있음)
    if (this.hasDuplicateBooking) {
      throw new Error('DUPLICATE_BOOKING')
    }

    return parsedEntity
  }

  /**
   * 아직 예약용 classId가 생성되지 않은 레슨 카드에서도 중복 예약을 먼저 차단한다.
   * classId 유효성 검증과 분리해야 placeholder 생성 후 /booking으로 진입하는 우회 경로가 생기지 않는다.
   */
  get hasDuplicateBooking(): boolean {
    const { regClassCount, isUnlimitedTicket } = this.regClassCountByLanguage

    return isUnlimitedTicket && regClassCount > 0
  }

  /**
   * 언어별 예약 수 조회
   *
   * user entity에서 해당 언어 + 커리큘럼의 예약 수를 찾음
   */
  get regClassCountByLanguage() {
    const mappedCurriculumType = API_CURRICULUM_TYPE_MAP[this.curriculumType]

    const regClassCountByLanguage = this.user.regClassCountForLangTypeList.find(
      (item) => item.langType === this.lesson.langType && item.curriculumType === mappedCurriculumType,
    )

    if (!regClassCountByLanguage) {
      return { regClassCount: 0, isUnlimitedTicket: false }
    }

    return {
      regClassCount: regClassCountByLanguage.regClassCount,
      isUnlimitedTicket: regClassCountByLanguage.isUnlimitedTicket === 'Y',
    }
  }

  /**
   * 남은 티켓 수 조회
   *
   * 같은 언어의 티켓이 여러 장 공존할 수 있다(예: 예약에 묶여 잔여 0인 체험권 + 무제한권).
   * 첫 번째 티켓만 보면 무제한권이 있어도 잔여 0으로 오판하므로, 잔여가 가장 많은 티켓 기준으로 판단한다.
   */
  get remainTicketCount(): number {
    const sameLanguageTickets = this.tickets.filter((item) => item.langType === this.lesson.langType)

    if (sameLanguageTickets.length === 0) {
      return 0
    }

    return Math.max(...sameLanguageTickets.map((item) => item.remainCountNum))
  }

  /**
   * 예약 변경 가능 여부
   */
  canChangeBooking(currentTime: number): boolean {
    return !isChangeNotAllowedForLesson(this.lesson.unixClassStartDatetime, currentTime)
  }
}
